import { Button, Dropdown, Tag, notification } from 'antd';
import ErrorDetails from 'common/error/components/error-details';
import Spinner from 'common/spinner/components/spinner';
import { Interweave } from 'interweave';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Flex, Icon, SapId } from 'ui';
import useDeepCompareEffect from 'use-deep-compare-effect';
import moment from 'utils/cops-moment';
import ErrorHandler from '../../../common/error/components/error-handler';
import StartDateDialog from '../../../common/toolbar/components/start-date-dialog';
import {
  createAutoOrders,
  createAutoTickets,
  createDraftOrders,
  publishToSapQuickAction,
} from '../../../common/toolbar/containers/toolbar-dynamic-actions-actions';
// eslint-disable-next-line max-len
import PlannerGroupToggle from '../../../header/planner-group-toggle/components/planner-group-toggle';
import {
  getCampaignForLandingZone,
} from '../../../home/containers/landing-zone/landing-zone-actions';
// eslint-disable-next-line max-len
import { viewConfig } from '../../../common/gantt/config';
import QuestionWarningNotification from '../../../tickets/components/list/question-warning-notification';
import {
  getCategoryInfo,
} from '../../../tickets/containers/form/ticket-form-actions';
import { category, ticketActionIds } from '../../../tickets/ticket-config';
import { campaignActions, focusCampaignPrefix, multipleProcessQuickActions, publishSteps } from '../../../utils/app-configs';
import { isObjectNullOrEmpty } from '../../../utils/helpers';
import IconColumn from '../../../workgrid/components/iconcolumn';
import { getDurationsText, getStatusIcon } from '../../../workgrid/helpers/grid-helpers';

const StyledInfo = styled.span`
  margin: 0 3px;
`;

const Wrapper = styled.div`
  width: 100%;
  // Add height here to force vertical scrolling in the table
  overflow: auto;
  margin-top: 25px;
`;

const Table = styled.table`
  width: 100%;
`;

const HeaderColumn = styled.th`
  text-align: left;
  ${p => p.width ? `max-width: ${p.width}px` : 'width: auto'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 5px;
`;

const Column = styled.td`
  ${p => p.width ? `max-width: ${p.width}px` : 'width: auto'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 5px;
`;

const StyledIcon = styled(Icon)`
  ${p => p.style && Object.keys(p.style)?.length ? p.style : 'color: #999'};
`;

const Row = styled.tr`
  ${p => !p.isHeader &&
    `
    border-bottom: 1px solid rgba(5, 5, 5, 0.06);
    height: 40px;
    `
  };
`;

const Header = styled.thead`
  position: sticky;
  top: 0;
  background-color: white;
  border-bottom: 1px solid #B6BBBF;
`;

const Actions = styled(Dropdown)`
  background-color: ${p => p.disabled ? '#B6BBBF' : p.theme.colors.theme};
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  i {
    color: white;
    cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  }
`;

const Toggle = styled(Icon)`
  cursor: pointer;
  font-size: 12px;
  width: 20px;
  margin-right: 5px;
  color: #B6BBBF;
`;

const Instruction = styled.div`
  display: flex;
  flex-flow: row nowrap;
  height: ${p => p.height};
  i {
    padding-top: 3px;
  }
  span {
    overflow: hidden;
    height: ${p => p.textHeight};
  }
  .instruction-field ul {
    margin-bottom: 0px;
    &[data-checked=false] {
      padding-left: 20px;
      list-style: none;
      li::before {
        content: '\\2610';
        cursor: pointer;
        margin-right: 10px;
      } 
    }
    &[data-checked=true] {
      padding-left: 20px;
      list-style: none;
      li::before {
        content: '🗹';
        cursor: pointer;
        margin-right: 10px;
      } 
    }
  }
  .instruction-field a {
    text-decoration: underline;
  }
`;

const ErrorIconWrapper = styled.div`
  i {
    color: ${p => p.theme.colors.alerts[1]}
  }
`;

const PlannerGroupWrapper = styled.div`
  margin-top: ${p => p.isCollapsed ? 0 : 20}px;
  display: flex;
  justify-content: flex-end;
`;

const renderColumn = (c, meta) => {
  if (c.id === 'Watchers') {
    return {
      id: c.id,
      displayName: meta?.displayName,
      renderFn: row => <p>{row?.watchers?.map(watcher => watcher.displayName)?.join('; ')}</p>,
      width: c.width,
    };
  }
  if (c.id === 'OrderProcessStatus') {
    return {
      id: c.id,
      displayName: meta?.displayName,
      renderFn: row => <Flex rrn aic>
        {getStatusIcon(row.orderProcessStatus?.errorLevel)}
        <StyledInfo>{row.orderProcessStatus?.text}</StyledInfo>
      </Flex>,
      width: c.width,
    };
  } else if (c.id === 'AdditionalInfo') {
    return {
      id: c.id,
      displayName: meta?.displayName,
      renderFn: row => row?.additionalInfo?.length ?
        row?.additionalInfo?.map(v => <span key={v.id}>
          <StyledIcon
            className={!v.icon.includes('fa-') ? undefined : v.icon}
            type={v.icon.includes('fa-') ? undefined : v.icon}
            light={!v.icon.includes('solid') && !v.icon.includes('duotone')}
            style={v.iconStyle}
          />
          <StyledInfo> {v.description}</StyledInfo>
        </span>) :
        null,
      width: c.width,
    };
  } else if (c.id === 'LogisticsDate') {
    return {
      id: c.id,
      displayName: meta?.displayName,
      renderFn: row => <span>
        <StyledIcon
          type={row?.logisticsDate?.icon}
          style={row?.logisticsDate?.iconStyle}
          light={
            !row?.logisticsDate?.icon?.includes('solid') &&
            !row?.logisticsDate?.icon?.includes('duotone')
          }
        />
        <StyledInfo>{row?.logisticsDate?.description}</StyledInfo>
      </span>,
      width: c.width,
    };
  } else if (c.id === 'PlannedLabor' || c.id === 'LaborDuration' ||
    c.id === 'TravelDuration' || c.id === 'CalculatedPlannedLabor') {
    return {
      id: c.id,
      displayName: meta.displayName,
      renderFn: row => <p>{getDurationsText(row[meta.fieldName])}</p>,
      width: c.width,
    };
  } else if (meta?.type === 'date') {
    return {
      id: c.id,
      displayName: meta.displayName,
      renderFn: row => row[meta.fieldName] ? moment(row[meta.fieldName]).utc().format('L') : null,
      width: c.width,
    };
  } else if (meta?.type === 'sapId') {
    return {
      id: c.id,
      displayName: meta.displayName,
      renderFn: row => <SapId originalId={row[meta.fieldName]}>{id => <>{id}</>}</SapId>,
      width: c.width,
    };
  } else if (meta?.type === 'tags') {
    return {
      id: c.id,
      displayName: meta.displayName,
      renderFn: row => row[meta.fieldName]?.map(v => <Tag key={v.id} color='blue'>
        {v.description}
      </Tag>),
      width: c.width,
    };
  }
  return {
    id: c.id,
    displayName: meta?.displayName,
    fieldName: meta?.fieldName,
    width: c.width,
  };
};

const CampaignTile = ({
  runningCampaign,
  viewInfo,
  result,
  metadata,
  breakPoint,
  intl,
}) => {
  const dispatch = useDispatch();
  const [selectedAction, setSelectedAction] = useState();
  const [multipleProcess, setMultipleProcess] = useState();
  const [refreshAfterActionSuccess, setRefreshAfterActionSuccess] = useState(false);
  const [hideInstruction, setHideInstruction] = useState(false);
  const [plannerGroup, setPlannerGroup] = useState();
  const { isLoadingCampaignLandingZone } = useSelector(state => state.landingZone);

  const {
    createSuccess,
    deleteSuccess,
    generalError,
    errors,
    autoFaulty,
    autoTotal,
    isCreating,
    autoCreateFailed,
    planningBlocks,
  } = useSelector(state => state.dynamicToolbar);
  const {
    categoryInfo,
  } = useSelector(state => state.ticketForm);

  const handleQuestionResponse = (action, v) => {
    const actionsData = {
      ...action?.payload,
      tags: v,
    };
    dispatch(createAutoTickets({ actions: [actionsData] }));
    setRefreshAfterActionSuccess(true);
  };

  const changeMultipleProcess = () => {
    if (selectedAction?.id === 'updateOrderBackground') {
      if (!multipleProcess) {
        setMultipleProcess(multipleProcessQuickActions.processingOrders);
      }
      if (multipleProcess === multipleProcessQuickActions.processingOrders) {
        setMultipleProcess(multipleProcessQuickActions.publishToSap);
      }
    }
  };

  const getQuestionNotification = onConfirm => <QuestionWarningNotification
    intl={intl}
    onConfirm={onConfirm}
    questionData={categoryInfo?.conclusionWizard}
    tags={categoryInfo?.conclusionTags}
  />;

  useEffect(() => {
    setMultipleProcess(null);
  }, [selectedAction?.id]);

  useEffect(() => {
    const storage = JSON.parse(localStorage.getItem('homeLayout'));
    if (storage) {
      setHideInstruction(
        storage[breakPoint]?.find(e =>
          e.i === `${focusCampaignPrefix}_${runningCampaign.id}`,
        )?.hideInstruction,
      );
    }
    dispatch(getCategoryInfo({ id: category.diagnostics }));
    setMultipleProcess(null);
  }, []);

  useEffect(() => {
    if (autoTotal && (!errors || !Object.keys(errors)?.length) && autoCreateFailed?.length === 0) {
      setMultipleProcess(null);
      notification.success({
        message: intl.formatMessage({
          id: 'toolbar.AUTO_SUCCESS_GENERAL',
        }),
        key: 'campaignSuccess',
        placement: 'top',
      });
    } else if (autoCreateFailed?.length > 0) {
      setMultipleProcess(null);
      notification.error({
        key: 'campaignActionErrors',
        placement: 'top',
        message: <ErrorDetails
          errors={autoCreateFailed}
          autoFaulty={autoFaulty}
          autoTotal={autoTotal}
          // Antd does not support injectIntl inside a component
          // so we need to pass it from this component
          translate={intl.formatMessage}
        />,
        duration: 0,
      });
    } else if (autoTotal && errors && Object.keys(errors)?.length) {
      setMultipleProcess(null);
      notification.error({
        key: 'campaignActionErrors',
        placement: 'top',
        message: <ErrorDetails
          errors={errors}
          autoFaulty={autoFaulty}
          autoTotal={autoTotal}
          // Antd does not support injectIntl inside a component
          // so we need to pass it from this component
          translate={intl.formatMessage}
        />,
        duration: 0,
      });
    }
  }, [autoTotal]);

  useEffect(() => {
    const publishMoveToOrders = JSON.parse(localStorage.getItem('publishMoveToOrders'));
    if (selectedAction?.id === 'updateOrderBackground') {
      if (planningBlocks?.length && publishMoveToOrders &&
        multipleProcess === multipleProcessQuickActions.processingOrders
      ) {
        changeMultipleProcess();
        dispatch(publishToSapQuickAction({
          planningIds: planningBlocks?.map(pb => pb.id),
          boardId: viewConfig.boardIdTypes.service,
          isTimeline: false,
          publishWithQuickAction: true,
        }));
      }
      if (
        !publishMoveToOrders ||
        (publishMoveToOrders &&
          multipleProcess === multipleProcessQuickActions.publishToSap)
      ) {
        setMultipleProcess(null);
      }
    }
  }, [planningBlocks]);

  useDeepCompareEffect(() => {
    if (!isObjectNullOrEmpty(generalError) && refreshAfterActionSuccess) {
      setMultipleProcess(null);
      setRefreshAfterActionSuccess(false);
    }
  }, [generalError]);

  useEffect(() => {
    if (createSuccess || deleteSuccess) {
      if (selectedAction) {
        setSelectedAction(null);
      }
      if (refreshAfterActionSuccess) {
        const localData = JSON.parse(
          localStorage.getItem('preferredPlannerGroupByServiceCenterId') || '{}') ||
          {};
        const serviceCenterId = JSON.parse(localStorage.getItem('serviceCenter'))?.value || '';
        dispatch(getCampaignForLandingZone({
          campaignId: runningCampaign.id,
          plannerGroup: plannerGroup || localData[serviceCenterId],
        }));
        setRefreshAfterActionSuccess(false);
      }
    }
  }, [createSuccess, deleteSuccess]);

  const toggleInstruction = () => {
    setHideInstruction(s => !s);
    const storage = JSON.parse(localStorage.getItem('homeLayout'));
    if (storage) {
      let element = storage[breakPoint]?.find(s =>
        s.i === `${focusCampaignPrefix}_${runningCampaign.id}`,
      );
      if (element) {
        element = {
          ...element,
          hideInstruction: !hideInstruction,
        };
        const filtered = storage[breakPoint]?.filter(f =>
          f.i !== `${focusCampaignPrefix}_${runningCampaign.id}`,
        );
        storage[breakPoint] = [...filtered, element];
      }
    }
    localStorage.setItem('homeLayout', JSON.stringify(storage));
  };

  const handleAutoOrders = (action, newDate, workcenterId, hasSequentialOrder) => {
    dispatch(action.id === 'createDraftOrder' ?
      createDraftOrders({ actions: [action.payload], newDate }) :
      createAutoOrders({
        actions: [action.payload],
        isDelete: action.id === 'deleteDraftAction',
        workcenterId,
        newDate,
        hasSequentialOrder,
      }),
    );
    setRefreshAfterActionSuccess(true);
  };

  const handleRowAction = (id, action, row) => {
    const additionalActionParams = action ? campaignActions?.find(a => a.id === action.id) : null;
    if (id === 'open') {
      if (runningCampaign?.worklistType === 'ticket' && row?.number && row?.id) {
        window.open(`ticket/${row.number}/${row.id}`);
      } else if (row?.uri) {
        window.open(`/${row.uri}`);
      }
    } else if (id === 'refresh') {
      const localData = JSON.parse(
        localStorage.getItem('preferredPlannerGroupByServiceCenterId') || '{}',
      ) || {};
      const serviceCenterId = JSON.parse(localStorage.getItem('serviceCenter'))?.value || '';
      dispatch(getCampaignForLandingZone({
        campaignId: runningCampaign.id,
        plannerGroup: plannerGroup || localData[serviceCenterId],
      }));
    } else if (!additionalActionParams || additionalActionParams.skipDialog) {
      if (runningCampaign?.worklistType === 'ticket') {
        // close ticket
        if (action.payload.action === ticketActionIds.closeTicket &&
          action.payload.isDiagnosticsTicket) {
          if (action.payload?.hasRequestedSmartAction) {
            notification?.error({
              message: intl.formatMessage({ id: 'common.ERROR_OCCURED' }),
              description: <>
                <p>{intl.formatMessage({ id: 'tickets.error.saveError' })}</p>
                <ul>
                  <li>{intl.formatMessage(
                    { id: 'tickets.error.cannotMoveBeyondStatusInProgress' },
                  )}</li>
                </ul>
              </>,
              duration: 0,
              className: 'extended-notification',
            });
          } else {
            notification.warning({
              message: intl.formatMessage({ id: 'ticket.QUESTION_WARNING_TITLE' }),
              description: getQuestionNotification(
                v => handleQuestionResponse(action, v),
              ),
              key: 'openQuestionsWarning',
              style: {
                width: 676,
                marginLeft: -100,
              },
              className: 'extended-notification',
              duration: 0,
              placement: 'top',
            });
          }
        } else {
          dispatch(createAutoTickets({ actions: [action.payload] }));
          setRefreshAfterActionSuccess(true);
        }
      } else {
        handleAutoOrders(action);
      }
    } else {
      setSelectedAction({
        ...action,
        ...additionalActionParams,
      });
    }
  };

  const columns = [
    {
      id: 'actions',
      renderFn: row => <Actions
        disabled={
          row.operationInfo?.status >= publishSteps.unknown.status &&
          row.operationInfo?.status < publishSteps.done.status
        }
        menu={{
          items: [
            {
              id: 'open',
              description: intl.formatMessage({ id: 'campaigns.actions.OPEN' }),
              fixed: true,
            },
            ...runningCampaign.actions || [],
            {
              id: 'refresh',
              description: intl.formatMessage({ id: 'campaigns.actions.REFRESH_LIST' }),
              fixed: true,
            },
          ]?.map(a =>
          ({
            key: a.id,
            label: a.description,
            disabled: !a.fixed && !row.actions.find(r => a.id === r.id),
          })),
          onClick: (e) => {
            handleRowAction(e.key, row.actions?.find(a => a.id === e.key), row);
          },
        }}
      >
        <Button><Icon type='ellipsis' size='lg' /></Button>
      </Actions>,
      width: 100,
    },
    {
      id: 'errors',
      renderFn: row => <ErrorIconWrapper>
        {
          row.operationInfo?.errors?.length ||
            row.operationInfo?.isStale ||
            row.operationInfo?.isTimedOut ? <Icon type='circle-exclamation' size='sm' /> : null
        }
      </ErrorIconWrapper>,
      width: 20,
    },
    ...viewInfo?.columnInfo?.sort((a, b) => a.index - b.index)?.map(c =>
      renderColumn(
        c,
        metadata?.columnDefinitions?.find(m => m.id?.toLowerCase() === c.id?.toLowerCase())),
    ),
    {
      id: 'info',
      displayName: 'Info',
      width: 150,
      renderFn: row => row ? <IconColumn data={row} /> : null,
    },
  ];

  const handlePlannerGroupChange = (value) => {
    setPlannerGroup(value);
    dispatch(getCampaignForLandingZone({
      campaignId: runningCampaign.id,
      plannerGroup: value,
    }));
  };

  const renderContent = () => {
    if (isLoadingCampaignLandingZone) {
      return <Spinner />;
    }
    return result.data?.length ?
      <Wrapper>
        <Table>
          <Header>
            <Row isHeader={true}>
              {columns?.map(column =>
                <HeaderColumn key={column.id} width={column.width}>
                  {column.displayName}
                </HeaderColumn>,
              )}
            </Row>
          </Header>
          <tbody>
            {result.data?.map((row, rowIndex) =>
              <Row key={rowIndex}>
                {columns?.map(column =>
                  <Column key={column.id} width={column.width}>
                    {column.renderFn ? column.renderFn(row) : row[column.fieldName]}
                  </Column>,
                )}
              </Row>,
            )}
          </tbody>
        </Table>
      </Wrapper> :
      <FormattedMessage id='table.NO_DATA' />;
  };

  return (
    <>
      <ErrorHandler type='notification' error={generalError} />
      {runningCampaign?.instruction ?
        <Instruction
          height={hideInstruction ? '14px' : 'auto'}
          textHeight={hideInstruction ? '10px' : 'auto'}
        >
          <Toggle
            onClick={toggleInstruction}
            type={hideInstruction ? 'chevron-right' : 'chevron-down'}
          />
          <Interweave content={runningCampaign?.instruction} className='instruction-field' />
        </Instruction> :
        null
      }
      {runningCampaign?.groupByColumn ?
        <PlannerGroupWrapper isCollapsed={hideInstruction}>
          <PlannerGroupToggle
            onChange={value => handlePlannerGroupChange(value)}
            extraStyles='width: 150px;'
          />
        </PlannerGroupWrapper> :
        null
      }
      {renderContent()}
      <StartDateDialog
        changeMultipleProcess={changeMultipleProcess}
        multipleProcess={multipleProcess}
        selectedActions={[selectedAction?.id]}
        visible={selectedAction && !selectedAction.skipDialog}
        onSubmit={(newDate, workcenterId, hasSequentialOrder) =>
          handleAutoOrders(selectedAction, newDate, workcenterId, hasSequentialOrder)
        }
        onClose={() => {
          setMultipleProcess(null);
          setSelectedAction(null);
        }}
        loading={isCreating}
        needsTime={selectedAction?.needsTime}
        needsWorkcenter={selectedAction?.needsWorkcenter}
        serviceCenterId={selectedAction?.payload?.serviceCenterId}
      />
    </>
  );
};

CampaignTile.propTypes = {
  runningCampaign: PropTypes.object,
  viewInfo: PropTypes.object,
  result: PropTypes.object,
  metadata: PropTypes.object,
  breakPoint: PropTypes.string,
  intl: PropTypes.object,
};

export default injectIntl(CampaignTile);
